<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('schedule.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        {
          text: $t('schedule.pageTitle'),
          href: '/schedules'
        },
        {
          text: $t('add_new'),
          active: true
        }
      ]" />
    </div>
    <div class="row">
      <div class="col-sm-12">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loadingPreData">
              <form class="form-horizontal">
                <div class="row mb-3">
                  <div class="col-sm-12 col-lg-6 col-md-12">
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label required">{{ $t('schedule.academicYear')
                      }}</label>
                      <div class="col-md-7">
                        <treeselect :load-options="loadAcYearOptions" :options="acYearOptions"
                          :placeholder="$t('typeForSearch')" v-model="model.ac_year_id" />
                        <div class="validate" v-if="errors.has('ac_year_id')">
                          {{ errors.first('ac_year_id') }}
                        </div>
                      </div>
                    </div>
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label required">{{ $t('schedule.programMain')
                      }}</label>
                      <div class="col-md-7">
                        <treeselect :load-options="loadProgramMain" :options="acProgramMainOptions"
                          :placeholder="$t('typeForSearch')" v-model="model.acp_id" />
                        <div class="validate" v-if="errors.has('acp_id')">
                          {{ errors.first('acp_id') }}
                        </div>
                      </div>
                    </div>
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label required">{{ $t('schedule.programSub')
                      }}</label>
                      <div class="col-md-7">
                        <treeselect :load-options="loadProgramSub" :options="acProgramSubOptions"
                          :placeholder="$t('typeForSearch')" v-model="model.acps_id" @input="loadScheduleTime" />
                        <div class="validate" v-if="errors.has('acps_id')">
                          {{ errors.first('acps_id') }}
                        </div>
                      </div>
                    </div>
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label">{{ $t('schedule.level') }}</label>
                      <div class="col-md-7">
                        <a-select v-model="model.level_id" :options="levels" :filter-option="(input, option) =>
                          option.componentOptions.children[0].text
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                          "></a-select>
                      </div>
                    </div>
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label required">{{ $t('schedule.className')
                      }}</label>
                      <div class="col-md-7">
                        <treeselect :load-options="loadClass" :options="acClassOptions"
                          :placeholder="$t('typeForSearch')" v-model="model.class_id" />
                        <div class="validate" v-if="errors.has('class_id')">
                          {{ errors.first('class_id') }}
                        </div>
                      </div>
                    </div>
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label required">
                        {{ $t('schedule.studyShift')
                      }}
                      </label>
                      <div class="col-md-7">
                        <treeselect v-model="model.weekday_type_id" :options="studyShiftOptions"
                          :placeholder="$t('select')" :clearable="false" @input="onShiftChange" />
                      </div>
                    </div>

                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label">
                        {{ $t('schedule.dayFrom') }}
                      </label>
                      <div class="col-md-7">
                        <select v-model.number="model.wd_id_from" class="form-control"
                          :class="{ 'is-invalid': errors.has('wd_id_from') }">
                          <option value>{{ $t('select') }}</option>
                          <option v-for="(wd, index) in filteredWeekDays" :key="index" :value="wd.wd_id">
                            {{ wd.wd_name_en }}
                          </option>
                        </select>
                        <div class="invalid-feedback" v-if="errors.has('wd_id_from')">
                          {{ errors.first('wd_id_from') }}
                        </div>
                      </div>
                    </div>

                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label">
                        {{ $t('schedule.dayTo') }}
                      </label>
                      <div class="col-md-7">
                        <select v-model.number="model.wd_id_to" class="form-control"
                          :class="{ 'is-invalid': errors.has('wd_id_to') }">
                          <option value>{{ $t('select') }}</option>
                          <option v-for="(wd, index) in filteredWeekDays" :key="index" :value="wd.wd_id">
                            {{ wd.wd_name_en }}
                          </option>
                        </select>
                        <div class="invalid-feedback" v-if="errors.has('wd_id_to')">
                          {{ errors.first('wd_id_to') }}
                        </div>
                      </div>
                    </div>
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label required">{{ $t('schedule.type') }}</label>
                      <div class="col-md-7">
                        <select v-model.number="model.sch_type" class="form-control"
                          :class="{ 'is-invalid': errors.has('sch_type') }" @change="loadScheduleTime">
                          <option value>{{ $t('select') }}</option>
                          <option v-for="(schType, index) in scheduleTypes" :key="index" :value="schType.sch_type">{{
                            schType.sch_type_name }}</option>
                        </select>
                        <div class="invalid-feedback" v-if="errors.has('sch_type')">
                          {{ errors.first('sch_type') }}
                        </div>
                      </div>
                    </div>
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label">{{ $t('schedule.date') }}</label>
                      <div class="col-md-7">
                        <a-range-picker format="DD-MM-YYYY" :defaultValue="rangeDate" :defaultPickerValue="rangeDate"
                          @change="dateRangeChange" style="width: 100%;">
                          <template slot="renderExtraFooter"></template>
                        </a-range-picker>
                      </div>
                    </div>
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label"></label>
                      <div class="col-md-7">
                        <ts-button class="btn-primary mr-2" @click.prevent="store" :waiting="saving">{{ $t('save')
                        }}</ts-button>
                        <ts-button color="primary" outline @click.prevent="saveAddNew" :waiting="saving_new">{{
                          $t('save_add_new') }}</ts-button>
                        <ts-button class="btn-default ml-2" @click="$router.push({ name: 'schedule' })">{{ $t('cancel')
                        }}</ts-button>
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-12 col-lg-6 col-md-12">
                    <div class="form-group row">
                      <label class="col-md-4 form-col-label control-label required">{{ $t('schedule.time') }}</label>
                      <div class="col-md-7">
                        <ts-loading-banner :loading="timeLoading" class="form-control time-list"
                          style="height: inherit; width: 100%;" :size="20">
                          <ul v-show="scheduleTimes.length">
                            <li v-for="(time, index) in scheduleTimes" :key="index">
                              <div class="ts-flex align-items-start">
                                <ts-checkbox v-model="model.sc_time_id_list" :value="time.sc_time_id">
                                  <span>{{ time.sc_from_time }} -
                                    {{ time.sc_to_time }}</span>
                                  <span class="ml-2" v-show="time.sc_from_time1 !== null &&
                                    time.sc_to_time1 !== null
                                    ">({{ time.sc_from_time1 }} -
                                    {{ time.sc_to_time1 }})</span>
                                </ts-checkbox>
                              </div>
                            </li>
                          </ul>
                        </ts-loading-banner>
                        <div class="validate" v-if="errors.has('sc_time_id_list')">
                          {{ errors.first('sc_time_id_list') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>
<script>
import { LOAD_ROOT_OPTIONS } from '@riophae/vue-treeselect'
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'

export default {
  name: 'schedule-create',
  props: ['schedule'],
  data() {
    return {
      model: {
        acp_id: null,
        acps_id: null,
        ac_year_id: null,
        class_id: null,
        sc_time_id_list: [],
        wd_id_from: '',
        wd_id_to: '',
        sch_type: '',
        override: false,
        level_id: null,
        weekday_type_id: null,
        date_from: moment().format('DD-MM-YYYY'),
        date_to: moment().format('DD-MM-YYYY'),

      },
      rangeDate: [moment(), moment()],
      errors: new Errors(),
      loadingPreData: false,
      saving: false,
      saving_new: false,
      acYearOptions: null,
      acProgramMainOptions: null,
      acProgramSubOptions: null,
      acClassOptions: null,
      timeLoading: false,
      scheduleTimes: [],
      levels: [],
      studyShiftOptions: [
        { id: 1, label: 'Weekday' },
        { id: 2, label: 'Weekend' },
      ],

    }
  },
  created() {
    this.fetchResourceBeforeCreate()
  },
  computed: {
    ...mapState('schedule', ['weekDays', 'scheduleTypes']),
    filteredWeekDays() {
      if (!this.model.weekday_type_id) return this.weekDays

      if (this.model.weekday_type_id === 1) {
        return this.weekDays.filter(wd => wd.wd_id >= 1 && wd.wd_id <= 5)
      } else if (this.model.weekday_type_id === 2) {
        return this.weekDays.filter(wd => wd.wd_id >= 6 && wd.wd_id <= 7)
      }

      return this.weekDays
    }

  },
  methods: {
    ...mapActions('schedule', [
      'fetchAcademicYear',
      'fetchProgramMain',
      'fetchProgramSub',
      'fetchClass',
      'listTime',
      'getLevel'
    ]),
    fetchResourceBeforeCreate() {
      this.loadingPreData = true
      this.$store
        .dispatch('schedule/dataBeforeCreate')
        .then(() => {
          this.loadingPreData = false
        })
        .catch(error => {
          this.loadingPreData = false
          this.$toasted.error(error.message)
        })
    },
    async loadAcYearOptions({ action /*, callback*/ }) {
      if (action === LOAD_ROOT_OPTIONS) {
        this.acYearOptions = null
        let response = await this.fetchAcademicYear()
        this.acYearOptions = response.data.map(el => ({
          id: el.ac_year_id,
          label: el.ac_year
        }))
      }
    },
    async loadProgramMain({ action /*, callback*/ }) {
      if (action === LOAD_ROOT_OPTIONS) {
        this.acProgramMainOptions = null
        let response = await this.fetchProgramMain()
        this.acProgramMainOptions = response.data.map(el => ({
          id: el.acp_id,
          label: el.acp_name_foreign
        }))
      }
    },
    async loadProgramSub({ action /*, callback*/ }) {
      if (action === LOAD_ROOT_OPTIONS) {
        this.acProgramSubOptions = null
        if (this.model.acp_id) {
          await this.fetchProgramSub({
            acp_id: this.model.acp_id
          })
            .then(response => {
              this.acProgramSubOptions = response.data.map(el => ({
                id: el.acps_id,
                label: el.acps_name_foreign
              }))
            })
            .catch(error => {
              this.$toasted.error(error.message)
            })
        }
      }
    },
    async loadClass({ action }) {
      if (action === LOAD_ROOT_OPTIONS) {
        this.acClassOptions = null
        if (this.model.acp_id) {
          await this.fetchClass({
            acp_id: this.model.acp_id,
            ac_year_id: this.model.ac_year_id
          })
            .then(response => {
              this.acClassOptions = response.data.map(el => ({
                id: el.class_id,
                label: el.class_name
              }))
            })
            .catch(error => {
              this.$toasted.error(error.message)
            })
        }
      }
    },
    async getLevel(acps_id) {
      return this.$store.dispatch('schedule/getLevel', {
        filter: {
          acps_id: acps_id
        }
      })
        .then(response => {
          this.levels = response.data.map(el => ({
            value: el.level_id,
            label: el.level_name_foreign
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },

    async store() {
      this.errors = new Errors()
      this.saving = true

      this.$store
        .dispatch('schedule/store', this.model)
        .then(response => {
          this.clearForm()
          this.saving = false
          this.$toasted.success(response.message)
          this.$router.push({ name: 'schedule' })
        })
        .catch(error => {
          this.saving = false
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
    },
    async saveAddNew() {
      this.errors = new Errors()
      this.saving_new = true

      this.$store
        .dispatch('schedule/store', this.model)
        .then(response => {
          this.clearForm()
          this.saving_new = false
          this.$toasted.success(response.message)
        })
        .catch(error => {
          this.saving_new = false
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
    },
    clearForm() {
      this.model = {
        acp_id: null,
        acps_id: null,
        ac_year_id: null,
        class_id: null,
        sc_time_id_list: [],
        wd_id_from: '',
        wd_id_to: '',
        sch_type: '',
        override: false,
        date_from: moment().format('DD-MM-YYYY'),
        date_to: moment().format('DD-MM-YYYY'),
      }
    },
    loadScheduleTime() {
      this.model.sc_time_id_list = []
      this.scheduleTimes = []

      this.timeLoading = true

      this.listTime(this.model).then(schTimes => {
        this.scheduleTimes = schTimes
        this.timeLoading = false
      })
    },
    dateRangeChange(dateRange) {
      this.model.date_from = dateRange[0].format('DD-MM-YYYY')
      this.model.date_to = dateRange[1].format('DD-MM-YYYY')
    },
    onShiftChange() {
      this.model.wd_id_from = ''
      this.model.wd_id_to = ''
      this.model.class_ids = []
      this.acClassOptions = null
    }
  },
  watch: {
    'model.acp_id': function (value) {
      this.model.acps_id = null
      this.acProgramSubOptions = null
      if (value) {
        this.fetchProgramSub({ acp_id: value })
      }
    },
    'model.acps_id': function (value) {
      this.model.class_id = null
      this.acClassOptions = null
      if (value) {
        this.fetchClass({ acps_id: value })
        this.getLevel(value)
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (_.isEmpty(to.params) && to.name != 'schedule') {
      this.$store.commit('schedule/RECEIVE_ACADEMIC_YEARS', [])
      this.$store.commit('schedule/RECEIVE_ACADEMIC_PROGRAM_MAINS', [])
      this.$store.commit('schedule/RECEIVE_ACADEMIC_PROGRAM_SUBS', [])
      this.$store.commit('schedule/RECEIVE_CLASSES', [])
      this.$store.commit('schedule/SET_WEEKDAYS', [])
      this.$store.commit('schedule/SET_SCHEDULE_TYPES', [])
    }
    next()
  }
}
</script>
<style lang="scss" scoped>
.time-list {
  overflow-y: auto;
  max-height: 300px;
  border: solid 1px #d2d6de;
  padding: 6px 12px;
  min-height: 34px;

  ul {
    list-style-type: none;
    padding: 0;
  }
}
</style>
